import Script from 'next/script';
import { useEffect, useState } from 'react';

import { smartAppBannerCloseIcon } from '../icons/plain/smartBannerCloseIcon';
import {
  parseSmartAppBanner,
  smartAppBannerCloseId,
  smartAppBannerHiddenClass,
  smartAppBannerId,
} from '~/utilities/constants/smartBanner';
import { useConsentContext } from '~/utilities/context/dynamic/ConsentContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';

export const SmartBanner = () => {
  const { hasFullConsent } = useConsentContext();
  const { country, language } = useAppContext();
  const [smartBannerLoaded, setSmartBannerLoaded] = useState(false);
  const [smartBannerLink, setSmartBannerLink] = useState('');

  const {
    configuration: { enableUniversalLinks, appStoreName },
  } = useStaticContext();

  useEffect(() => {
    const smartBanner =
      enableUniversalLinks && hasFullConsent
        ? parseSmartAppBanner(appStoreName || '', `${language}_${country.toUpperCase()}`)
        : '';

    setSmartBannerLink(smartBanner);
  }, [appStoreName, country, enableUniversalLinks, hasFullConsent, language]);

  // FS-4856 The Highstreet script injects its banner outside the React tree and only runs once,
  // so hide it while no page renders this component instead of relying on React to remove it.
  useEffect(() => {
    const root = document.documentElement;

    root.classList.remove(smartAppBannerHiddenClass);

    const smartBannerApp = document.getElementById(smartAppBannerId);

    if (smartBannerApp) {
      root.style.setProperty('--smart-banner-height', `${smartBannerApp.offsetHeight}px`);
    }

    return () => {
      root.classList.add(smartAppBannerHiddenClass);
      root.style.setProperty('--smart-banner-height', '0px');
    };
  }, []);

  useEffect(() => {
    const bodyEl = document.querySelector('body');
    const onSmartBannerAppUpdated = (_: MutationRecord[], observer: MutationObserver) => {
      const smartBannerApp = document.getElementById(smartAppBannerId);

      if (
        smartBannerApp &&
        smartBannerApp.style.display !== 'none' &&
        smartBannerApp.style.height !== 'auto'
      ) {
        setSmartBannerLoaded(true);
        document.documentElement.style.setProperty(
          '--smart-banner-height',
          `${smartBannerApp.offsetHeight}px`
        );

        const closeBtn = document.getElementById(smartAppBannerCloseId);

        if (smartBannerLoaded && closeBtn) {
          observer.disconnect();
          closeBtn.addEventListener('click', () => {
            setSmartBannerLoaded(false);
            document.documentElement.style.setProperty('--smart-banner-height', `0px`);
          });
          closeBtn.innerHTML = smartAppBannerCloseIcon;
        }
      }
    };

    const mutationObserver = new MutationObserver(onSmartBannerAppUpdated);

    if (bodyEl) {
      mutationObserver.observe(bodyEl, { attributes: true, childList: true, subtree: true });
    }

    return () => mutationObserver.disconnect();
  }, [smartBannerLoaded]);

  if (hasFullConsent && smartBannerLink) {
    return (
      <Script
        src={smartBannerLink}
        strategy="afterInteractive"
        onError={() => setSmartBannerLoaded(false)}
      />
    );
  }

  return null;
};
